import { Signal } from '@angular/core';

// ─── Shared model types ───────────────────────────────────────────────────────

export interface BesharpChatHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  reasoningSteps?: BesharpChatHistoryReasoningStep[];
}

export interface BesharpChatHistoryReasoningStep {
  type: 'tool_call' | 'tool_result' | 'thinking' | 'agent_thinking';
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export type BesharpChatStreamEventType =
  | 'text_chunk'
  | 'tool_call'

  // A sub-agent's own tokens, streamed while it works. Appends to the live step it opened
  // rather than adding one per chunk; never part of the assistant's answer.
  | 'sub_agent_thinking'
  | 'tool_result'
  | 'agent_thinking'
  | 'content_reset'
  | 'trace_id'
  // The request was refused before any token was spent: daily quota used up (429), or the
  // quota could not be checked (503). `content` is the message; the prompt was not sent.
  | 'quota_exceeded'
  | 'quota_unavailable'
  | 'error'
  | 'end';

export interface BesharpChatStreamEvent {
  type: BesharpChatStreamEventType;
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/**
 * One entry a user can pin in the composer with `@`.
 *
 * `code` is the identity and the only part the agent is given; `label` is what the user
 * reads and is deliberately not unique — 87 of the 307 indicators share a label, and
 * `Fixed-telephone subscriptions` exists four times under different units — so the menu
 * must always show `path` and `unit` alongside it.
 */
export interface BesharpChatReference {
  kind: 'indicator' | 'country' | 'region';
  code: string;
  label: string;
  path: string;
  unit: string;
  years: string;
  disaggregations: string[];
  capital: string;
}

/** The user's daily token quota, as shown by the usage meter next to the composer. */
export interface BesharpChatQuota {
  unlimited: boolean;
  /** Credits allowed per UTC day; null when unlimited. */
  limit: number | null;
  /** Credits used today. */
  used: number;
  /** 0–100, null when unlimited. */
  percent: number | null;
  exhausted: boolean;
  /** When the counter resets (next 00:00 UTC), ISO 8601. */
  resetsAt: string;
  /** The last days up to today, oldest first, each as a share of the daily limit. */
  history: { day: string; percent: number; turns: number }[];
}

export interface BesharpChatSendParams {
  message: string;
  sessionId?: string;
  model?: string;
  max_turns?: number;
}

// ─── Abstract service ─────────────────────────────────────────────────────────

/**
 * Abstract base class that `BesharpChatComponent` depends on.
 *
 * Extend this class in any concrete service to plug a custom backend into
 * the chat component:
 *
 * ```typescript
 * @Injectable({ providedIn: 'root' })
 * export class MyChatService extends BesharpChatService {
 *   async getConversationHistory(sessionId?: string) { ... }
 *   async *sendMessageStream(params: BesharpChatSendParams) { ... }
 * }
 * ```
 *
 * Then pass the instance to the component via the `service` input:
 *
 * ```html
 * <app-besharp-chat [service]="myChatService" [config]="cfg" />
 * ```
 */
export abstract class BesharpChatService {
  /**
   * Returns the conversation history for the given session.
   * Return an empty array if the session has no history or the ID is absent.
   */
  abstract getConversationHistory(sessionId?: string): Promise<BesharpChatHistoryMessage[]>;

  /**
   * Streams events for a single invocation.
   * Must yield `BesharpChatStreamEvent` objects and eventually yield an event
   * with `type === 'end'` (or `type === 'error'`) to signal completion.
   */
  abstract sendMessageStream(params: BesharpChatSendParams): AsyncGenerator<BesharpChatStreamEvent>;

  /**
   * Returns the indicators and countries the composer offers under `@`.
   *
   * Optional: a backend that does not implement it simply gets no mention menu, and the
   * composer keeps working as a plain text box.
   */
  getReferences?(): Promise<BesharpChatReference[]>;

  /**
   * Submits thumbs up/down feedback for a completed turn, identified by the `trace_id`
   * carried on a `trace_id` stream event.
   *
   * Optional: a backend that does not implement it simply gets no feedback buttons.
   */
  submitFeedback?(traceId: string, value: boolean): Promise<void>;

  /**
   * The current user's daily quota, kept up to date by the service (on load, after every
   * turn, and when the day resets). `null` until known.
   *
   * Optional: a backend without quotas simply shows no usage meter.
   */
  readonly quota?: Signal<BesharpChatQuota | null>;

  /** Reloads `quota` from the backend. */
  refreshQuota?(): Promise<void>;
}
